import React, { useEffect, useState } from "react";
import banner from "../../assets/images/trustworthy-about-banner.jpg";
import { FaEnvelope, FaMapMarkerAlt, FaPhoneAlt } from "react-icons/fa";
import { Col, Container, Row } from "react-bootstrap";
import { getCategoryList, submitContactUsFrom, websiteDetails } from "../server/api";

const ContactUs = () => {
  const [category, setCategory] = useState([]);
  const [siteDetails, setWebsiteDetails] = useState({});
  const [loading, setLoading] = useState({
    categories: false,
    website: false,
    submit: false
  });
  
  // Fetch Categories
  const fetchCategory = async () => {
    setLoading(prev => ({ ...prev, categories: true }));
    try {
      const res = await getCategoryList();
      if (res.status === true) {
        setCategory(res.data || []);
      } else {
        console.warn("Failed to fetch categories:", res.message);
      }
    } catch (error) {
      console.error("category API Error:", error);
    } finally {
      setLoading(prev => ({ ...prev, categories: false }));
    }
  };

  // Fetch Website Details
  const fetchWebsiteDetails = async () => {
    setLoading(prev => ({ ...prev, website: true }));
    try {
      const res = await websiteDetails();
      if (res.status === true) {
        setWebsiteDetails(res.data || {});
      } else {
        console.warn("Failed to fetch website details:", res.message);
      }
    } catch (error) {
      console.error("Website Details API Error:", error);
    } finally {
      setLoading(prev => ({ ...prev, website: false }));
    }
  };

  useEffect(() => {
    fetchCategory();
    fetchWebsiteDetails();
  }, []);

  const [formData, setFormData] = useState({
    service: "",
    name: "",
    email: "",
    message: "",
    phone: "",
  });

  const [errors, setErrors] = useState({});
  const [successMsg, setSuccessMsg] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    let val = value;
    
    // Phone number validation - keep only digits
    if (name === "phone") {
      val = value.replace(/\D/g, "");
    }
    
    setFormData(prev => ({ 
      ...prev, 
      [name]: val 
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const tempErrors = {};
    
    if (!formData.service.trim()) tempErrors.service = "Service is required";
    if (!formData.name.trim()) tempErrors.name = "Name is required";
    
    if (!formData.email.trim()) {
      tempErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      tempErrors.email = "Please enter a valid email address";
    }
    
    if (!formData.message.trim()) tempErrors.message = "Message is required";
    
    if (!formData.phone.trim()) {
      tempErrors.phone = "Phone number is required";
    } else if (formData.phone.length < 10) {
      tempErrors.phone = "Phone number must be at least 10 digits";
    }
    
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg("");
    
    if (!validate()) {
      return;
    }

    setLoading(prev => ({ ...prev, submit: true }));
    
    try {
      console.log("Submitting form data:", formData);
      
      // Prepare data to send
      const payload = {
        service: formData.service,
        name: formData.name,
        email: formData.email,
        message: formData.message,
        phone: formData.phone,
      };
      
      // Call the API
      const res = await submitContactUsFrom(payload);
      console.log("API Response:", res);
      
      if (res.status === true) {
        setSuccessMsg("Form submitted successfully! We'll contact you soon.");
        
        // Reset form
        setFormData({
          service: "",
          name: "",
          email: "",
          message: "",
          phone: "",
        });
        setErrors({});
      } else {
        // Handle API error response
        alert(res.message || "Something went wrong! Please try again.");
      }
    } catch (error) {
      console.error("Submit Contact Form Error:", error);
      
      // Handle different types of errors
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const errorData = error.response.data;
        if (errorData.message) {
          alert(`Error: ${errorData.message}`);
        } else if (errorData.errors) {
          // Handle validation errors from server
          const serverErrors = errorData.errors;
          let errorMessage = "Please fix the following errors:\n";
          Object.keys(serverErrors).forEach(key => {
            errorMessage += `• ${serverErrors[key].join(", ")}\n`;
          });
          alert(errorMessage);
        } else {
          alert(`Error ${error.response.status}: ${error.response.statusText}`);
        }
      } else if (error.request) {
        // The request was made but no response was received
        alert("Network error. Please check your internet connection and try again.");
      } else {
        // Something happened in setting up the request that triggered an Error
        alert("An error occurred. Please try again.");
      }
    } finally {
      setLoading(prev => ({ ...prev, submit: false }));
    }
  };

  // Auto hide success message
  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => {
        setSuccessMsg("");
      }, 5000); // 5 seconds
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  return (
    <div className="contact_us">
      <section className="inner-banner">
        <img src={banner} alt="About Us Banner" className="inner-banner-img" />
        <div className="inner-banner-content">
          <div className="container">
            <div className="inner-banner-content-in">
              <h1>Contact Us</h1>
              <p>Best Home Servicing Company for Household Repairs or Servicing</p>
            </div>
          </div>
        </div>

        <Container>
          <Row className="contact-det">
            <Col md={4}>
              <div className="con-in">
                <div className="con-icon"><FaPhoneAlt /></div>
                <div className="con-in-det">
                  <h6>Give Us A Call</h6>
                  <p>
                    {siteDetails.phone ? (
                      <a href={`tel:${siteDetails.phone}`}>{siteDetails.phone}</a>
                    ) : (
                      <span>Loading...</span>
                    )}
                  </p>
                </div>
              </div>
            </Col>

            <Col md={4}>
              <div className="con-in">
                <div className="con-icon"><FaEnvelope /></div>
                <div className="con-in-det">
                  <h6>Send Us A Message</h6>
                  <p>
                    {siteDetails.email ? (
                      <a href={`mailto:${siteDetails.email}`}>{siteDetails.email}</a>
                    ) : (
                      <span>Loading...</span>
                    )}
                  </p>
                </div>
              </div>
            </Col>

            <Col md={4}>
              <div className="con-in">
                <div className="con-icon"><FaMapMarkerAlt /></div>
                <div className="con-in-det">
                  <h6>Our Address</h6>
                  <p>{siteDetails.address || "Loading..."}</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="business-intro">
        <div className="container">
          <div className="row align-items-center">
            {/* LEFT CONTENT */}
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 className="mb-3"><span>Get in Touch with Sevasati</span></h2>
              <p className="mb-4">
                Sevasati is here to support societies, residents, and service partners
                with reliable digital solutions. Whether you have questions, need assistance,
                or want to collaborate with us, our team is always ready to help you
                with quick and transparent support.
              </p>

              <div className="contact_left d-flex align-items-center justify-content-between rounded">
                <p className="mb-0 fw-semibold">Do you need urgent help? Contact me</p>
                <a href="/contact-us" className="primary_btn btn btn-primary">Contact Me</a>
              </div>
            </div>

            {/* RIGHT FORM */}
            <div className="col-lg-6">
              <div className="contact-form-box">
                <form onSubmit={handleSubmit} noValidate>
                  {/* Success Message */}
                  {successMsg && (
                    <div className="alert alert-success d-flex justify-content-between align-items-center">
                      <span>{successMsg}</span>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setSuccessMsg("")}
                        aria-label="Close"
                      ></button>
                    </div>
                  )}

                  {/* Service Select */}
                  <div className="mb-3">
                    <select
                      className={`form-select ${errors.service ? "is-invalid" : ""}`}
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      disabled={loading.categories}
                    >
                      <option value="">Select Services</option>
                      {loading.categories ? (
                        <option value="" disabled>Loading services...</option>
                      ) : (
                        category.map((item) => (
                          <option key={item.id || item._id} value={item.id || item._id}>
                            {item.title || item.name}
                          </option>
                        ))
                      )}
                    </select>
                    {errors.service && (
                      <div className="invalid-feedback d-block">{errors.service}</div>
                    )}
                  </div>

                  {/* Name & Email */}
                  <div className="row">
                    <div className="col-md-6 mb-4">
                      <input
                        type="text"
                        className={`form-control ${errors.name ? "is-invalid" : ""}`}
                        placeholder="Name*"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        disabled={loading.submit}
                      />
                      {errors.name && (
                        <div className="invalid-feedback d-block">{errors.name}</div>
                      )}
                    </div>

                    <div className="col-md-6 mb-4">
                      <input
                        type="email"
                        className={`form-control ${errors.email ? "is-invalid" : ""}`}
                        placeholder="Email*"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={loading.submit}
                      />
                      {errors.email && (
                        <div className="invalid-feedback d-block">{errors.email}</div>
                      )}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="mb-4">
                    <textarea
                      className={`form-control ${errors.message ? "is-invalid" : ""}`}
                      rows="4"
                      placeholder="Message*"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      disabled={loading.submit}
                    ></textarea>
                    {errors.message && (
                      <div className="invalid-feedback d-block">{errors.message}</div>
                    )}
                  </div>

                  {/* Phone & Submit */}
                  <div className="row">
                    <div className="col-md-6 mb-4">
                      <input
                        type="tel"
                        className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                        placeholder="Phone Number*"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        disabled={loading.submit}
                      />
                      {errors.phone && (
                        <div className="invalid-feedback d-block">{errors.phone}</div>
                      )}
                    </div>

                    <div className="col-md-6 mb-4 d-grid">
                      <button 
                        type="submit" 
                        className="primary_btn btn btn-primary"
                        disabled={loading.submit}
                      >
                        {loading.submit ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Sending...
                          </>
                        ) : (
                          "Send Message"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;